import React, { useState, useEffect } from "react";
import { DataTable, Badge, Pagination } from "../../../components/ui";
import { fmt } from "../../../lib/formatters";
import { Eye, Edit, ShoppingBag, Calendar, User, Plus, Package, Download } from "lucide-react";

export const SalesTable = ({ sales, onView, onEdit, onDownload, onPay }) => {
  const [page, setPage] = useState(1);
  const itemsPerPage = 10;

  useEffect(() => {
    setPage(1);
  }, [sales]);

  const columns = [
    {
      header: "Type",
      render: (s) => (
        <span className={`px-2 py-0.5 rounded text-[10px] font-normal uppercase border ${
          s.recordType === 'material_request' ? 'bg-orange-50 text-orange-600 border-orange-100' : 'bg-blue-50 text-blue-600 border-blue-100'
        }`}>
          {s.recordType === 'material_request' ? 'Project' : 'Retail'}
        </span>
      )
    },
    {
      header: "Ref #",
      render: (s) => (
        <div className="flex items-center gap-2">
          <div className={`w-8 h-8 rounded bg-slate-50 flex items-center justify-center border border-slate-100 ${
            s.recordType === 'material_request' ? 'text-orange-400' : 'text-blue-400'
          }`}>
            {s.recordType === 'material_request' ? <Package size={14} /> : <ShoppingBag size={14} />}
          </div>
          <span className="text-[11px] font-bold text-slate-700 tracking-wider uppercase">{s.invoiceNo}</span>
        </div>
      )
    },
    {
      header: "Date",
      render: (s) => (
        <div className="flex items-center gap-1.5 text-slate-500">
           <Calendar size={13} className="text-slate-400" />
           <span className="text-[12px] font-medium">
            {new Date(s.date).toLocaleDateString()}
          </span>
        </div>
      )
    },
    {
      header: "Customer / Project",
      minWidth: '180px',
      render: (s) => (
        <div className="flex flex-col">
          <span className="font-semibold text-slate-800 text-[12px] truncate block max-w-[200px] uppercase">{s.customer || '—'}</span>
          <span className="text-[10px] text-slate-400 font-normal uppercase tracking-wider">{s.projectId || s.customerId || s.customerPhone || ''}</span>
        </div>
      )
    },
    {
      header: "Items & Qty",
      minWidth: '180px',
      render: (s) => (
        <div className="flex flex-col max-w-[200px]">
          <span className="font-semibold text-slate-800 text-[12px] truncate block max-w-[200px]" title={s.itemSummary}>{s.itemSummary || '—'}</span>
          <span className="text-[10px] font-medium text-slate-400 uppercase tracking-tight">TOTAL QTY:{s.totalQty || 0}</span>
        </div>
      )
    },
    {
      header: "Amount",
      render: (s) => (
        <span className="text-[12px] font-medium text-slate-600">
          {fmt(s.netPayable)}
        </span>
      )
    },
    {
      header: "Status",
      render: (s) => {
        let st = (s.recordType === 'material_request' ? (s.displayStatus || s.status) : s.status)?.toUpperCase();
        
        if (s.recordType === 'sale') {
           const paid = s.paidAmount || 0;
           const total = s.netPayable || 0;
           if (paid >= total && total > 0) st = 'COMPLETED';
           else if (paid > 0) st = 'PARTIAL';
           else st = 'PENDING';
        }

        let cls = 'bg-slate-100 text-slate-600 border-slate-200';
        if (st === 'DELIVERED') cls = 'bg-emerald-50 text-emerald-600 border-emerald-100';
        else if (st === 'DISPATCHED') cls = 'bg-orange-50 text-orange-600 border-orange-100';
        else if (st === 'PAID' || st === 'COMPLETED') cls = 'bg-emerald-50 text-emerald-600 border-emerald-100';
        else if (st === 'PARTIAL') cls = 'bg-blue-50 text-brand-blue border-blue-100';
        else if (st === 'PENDING') cls = 'bg-orange-50 text-orange-600 border-orange-100';

        return (
          <span className={`px-3 py-1 rounded-full text-[10px] font-medium uppercase border ${cls}`}>
            {st || 'PENDING'}
          </span>
        );
      }
    },
    {
      header: "Action",
      className: "text-center w-24",
      render: (s) => (
        <div className="flex items-center justify-center gap-1">
          <button 
            onClick={() => onView(s)} 
            className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-[#2b448f] hover:bg-slate-50 rounded-md transition-all"
            title="View Details"
          >
            <Eye size={16} />
          </button>
          {s.recordType === 'sale' && (!s.paidAmount || s.paidAmount < (s.netPayable || 0)) && (
            <button 
              onClick={() => onPay && onPay(s)} 
              className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-all"
              title="Record Payment"
            >
              <Plus size={16} />
            </button>
          )}
          <button 
            onClick={() => onDownload && onDownload(s)} 
            className="w-8 h-8 flex items-center justify-center text-slate-300 hover:text-indigo-600 hover:bg-indigo-50 rounded-md transition-all"
            title="Download Invoice"
          >
            <Download size={16} />
          </button>
        </div>
      )
    }
  ];



  const currentData = (sales || []).slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const totalPages = Math.max(Math.ceil((sales || []).length / itemsPerPage), 1);

  return (
    <div className="bg-white border border-slate-100 rounded-xl overflow-hidden shadow-sm">
      <DataTable columns={columns} data={currentData} noScroll={true} />
      
      {(sales || []).length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

